import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Camera, Calendar, Upload, IndianRupee, Star, Image as ImageIcon, Plus, Trash2, MapPin, ImagePlus, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import UserMenu from '../components/UserMenu';
import { auth, bookings as bookingsApi, services as servicesApi, photographers as photographersApi, assets as assetsApi } from '../lib/api';
import { formatINR, formatDate } from '../lib/auth';

const NEXT_STATUS = {
  pending: 'confirmed',
  confirmed: 'in_progress',
  in_progress: 'delivered',
  delivered: 'completed',
};

const STATUS_LABEL = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const emptyService = { title: '', category: '', price: '', description: '', delivery_days: '' };

const PhotographerDashboard = () => {
  const [searchParams] = useSearchParams();
  const [user, setUser] = React.useState(null);
  const [profile, setProfile] = React.useState(null);
  const [bookingList, setBookingList] = React.useState([]);
  const [serviceList, setServiceList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyBookingId, setBusyBookingId] = React.useState(null);
  const [newService, setNewService] = React.useState(emptyService);
  const [savingService, setSavingService] = React.useState(false);
  const [profileForm, setProfileForm] = React.useState(null);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(searchParams.get('tab') || 'bookings');

  React.useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const fileInputs = React.useRef({});

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, bookingsRes, servicesRes] = await Promise.all([
        auth.me(),
        bookingsApi.list(),
        servicesApi.list(),
      ]);
      setUser(meRes.data);
      setProfile(meRes.data.photographer_profile);
      setProfileForm(meRes.data.photographer_profile);
      setBookingList(bookingsRes.data.data || []);
      setServiceList(servicesRes.data || []);
    } catch (err) {
      toast.error('Could not load your dashboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleAdvanceStatus = async (booking) => {
    const next = NEXT_STATUS[booking.status];
    if (!next) return;
    setBusyBookingId(booking.id);
    try {
      await bookingsApi.updateStatus(booking.id, next);
      toast.success(`Booking marked as ${STATUS_LABEL[next]}`);
      loadAll();
    } catch {
      toast.error('Could not update booking status.');
    } finally {
      setBusyBookingId(null);
    }
  };

  const handleUploadClick = (bookingId) => {
    fileInputs.current[bookingId]?.click();
  };

  const handleFilesSelected = async (bookingId, fileList) => {
    if (!fileList.length) return;
    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append('files[]', file));
    setBusyBookingId(bookingId);
    try {
      await assetsApi.upload(bookingId, formData);
      toast.success(`${fileList.length} file(s) uploaded to the client's vault.`);
    } catch (err) {
      toast.error('Upload failed. Files may be too large or an unsupported type.');
    } finally {
      setBusyBookingId(null);
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!newService.title || !newService.price) {
      toast.error('Title and price are required.');
      return;
    }
    setSavingService(true);
    try {
      await servicesApi.create({
        ...newService,
        price: Number(newService.price),
        delivery_days: newService.delivery_days ? Number(newService.delivery_days) : null,
      });
      toast.success('Service added.');
      setNewService(emptyService);
      loadAll();
    } catch (err) {
      toast.error('Could not add this service.');
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async (id) => {
    try {
      await servicesApi.remove(id);
      toast.success('Service removed.');
      loadAll();
    } catch {
      toast.error('Could not remove this service.');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profile) return;
    setSavingProfile(true);
    try {
      const { data } = await photographersApi.update(profile.id, {
        business_name: profileForm.business_name,
        bio: profileForm.bio,
        city: profileForm.city,
        state: profileForm.state,
        starting_price: profileForm.starting_price ? Number(profileForm.starting_price) : null,
      });
      setProfile(data);
      setProfileForm(data);
      toast.success('Profile updated.');
    } catch {
      toast.error('Could not save your profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const coverInputRef = React.useRef(null);
  const avatarInputRef = React.useRef(null);
  const [uploadingCover, setUploadingCover] = React.useState(false);
  const [uploadingAvatar, setUploadingAvatar] = React.useState(false);

  const handleCoverFileSelected = async (file) => {
    if (!file || !profile) return;
    const formData = new FormData();
    formData.append('cover_photo', file);
    setUploadingCover(true);
    try {
      const { data } = await photographersApi.uploadCoverPhoto(profile.id, formData);
      setProfile(data);
      setProfileForm(data);
      toast.success('Cover image updated.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload that image.');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleAvatarFileSelected = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    setUploadingAvatar(true);
    try {
      const { data } = await auth.uploadAvatar(formData);
      setUser(data);
      if (data.photographer_profile) {
        setProfile(data.photographer_profile);
        setProfileForm(data.photographer_profile);
      }
      toast.success('Profile photo updated.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload that image.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const totalEarnings = bookingList.reduce((sum, b) => sum + Number(b.paid_amount || 0), 0);
  const upcomingCount = bookingList.filter((b) => ['pending', 'confirmed', 'in_progress'].includes(b.status)).length;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
            <div className="flex items-center space-x-4">
              <UserMenu user={user} />
            </div>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-2" data-testid="dashboard-title">
            Welcome Back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-lg text-muted-foreground" data-testid="dashboard-subtitle">
            Manage your bookings, upload assets, and grow your business
          </p>
          {profile && !profile.is_verified && (
            <Badge variant="secondary" className="mt-4">Your profile is pending admin verification and won't show in search yet</Badge>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="stats-grid">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Total Earnings <IndianRupee className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent><div className="font-playfair text-3xl font-bold" data-testid="stat-earnings">{formatINR(totalEarnings)}</div></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Total Bookings <Calendar className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-bookings">{bookingList.length}</div>
              <div className="text-xs text-muted-foreground">{upcomingCount} upcoming</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Average Rating <Star className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-playfair text-3xl font-bold" data-testid="stat-rating">{Number(profile?.avg_rating || 0).toFixed(1)}</div>
              <div className="text-xs text-muted-foreground">From {profile?.review_count || 0} reviews</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center justify-between">Active Services <ImageIcon className="w-4 h-4" /></CardTitle>
            </CardHeader>
            <CardContent><div className="font-playfair text-3xl font-bold" data-testid="stat-services">{serviceList.length}</div></CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">My Services</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <section data-testid="bookings-section" className="mt-6">
              {bookingList.length === 0 && (
                <Card><CardContent className="p-8 text-center text-muted-foreground">No bookings yet.</CardContent></Card>
              )}
              <div className="space-y-4">
                {bookingList.map((booking) => (
                  <Card key={booking.id} data-testid={`booking-${booking.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4 gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-playfair text-xl font-semibold" data-testid={`booking-type-${booking.id}`}>
                              {booking.service?.title || booking.event_type || 'Booking'}
                            </h3>
                            <Badge variant={booking.status === 'completed' ? 'default' : 'secondary'} className="capitalize" data-testid={`status-${booking.id}`}>
                              {STATUS_LABEL[booking.status]}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mb-2" data-testid={`client-${booking.id}`}>Client: {booking.client?.name}</div>
                          <div className="flex items-center text-sm text-muted-foreground gap-4 flex-wrap">
                            <span className="flex items-center"><Calendar className="w-4 h-4 mr-2" /><span data-testid={`date-${booking.id}`}>{formatDate(booking.event_date)}</span></span>
                            {booking.location && <span className="flex items-center"><MapPin className="w-4 h-4 mr-2" />{booking.location}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-playfair text-2xl font-bold" data-testid={`amount-${booking.id}`}>{formatINR(booking.total_amount)}</div>
                          <div className="text-xs text-muted-foreground">{Number(booking.paid_amount) >= Number(booking.total_amount) ? 'Paid' : 'Awaiting payment'}</div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-4 border-t flex-wrap">
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          ref={(el) => (fileInputs.current[booking.id] = el)}
                          onChange={(e) => handleFilesSelected(booking.id, e.target.files)}
                        />
                        <Button size="sm" onClick={() => handleUploadClick(booking.id)} disabled={busyBookingId === booking.id} data-testid={`upload-assets-${booking.id}`}>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Assets
                        </Button>
                        <Link to={`/vault/${booking.id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-details-${booking.id}`}>View Vault</Button>
                        </Link>
                        {NEXT_STATUS[booking.status] && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAdvanceStatus(booking)}
                            disabled={busyBookingId === booking.id}
                            data-testid={`advance-status-${booking.id}`}
                          >
                            Mark as {STATUS_LABEL[NEXT_STATUS[booking.status]]}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
              <div className="lg:col-span-2 space-y-4">
                {serviceList.length === 0 && <p className="text-muted-foreground">You haven't added any services yet - add one to start getting bookings.</p>}
                {serviceList.map((service) => (
                  <Card key={service.id} data-testid={`service-${service.id}`}>
                    <CardContent className="p-6 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold">{service.title}</h3>
                        {service.category && <Badge variant="outline" className="capitalize my-1">{service.category}</Badge>}
                        <p className="text-sm text-muted-foreground">{service.description}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-bold mb-2">{formatINR(service.price)}</div>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteService(service.id)} data-testid={`delete-service-${service.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card>
                <CardHeader><CardTitle>Add a Service</CardTitle></CardHeader>
                <CardContent>
                  <form className="space-y-3" onSubmit={handleAddService}>
                    <Input placeholder="Title (e.g. Wedding Package)" value={newService.title} onChange={(e) => setNewService({ ...newService, title: e.target.value })} data-testid="new-service-title" />
                    <Input placeholder="Category (e.g. wedding)" value={newService.category} onChange={(e) => setNewService({ ...newService, category: e.target.value })} data-testid="new-service-category" />
                    <Input type="number" placeholder="Price (INR)" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })} data-testid="new-service-price" />
                    <Input type="number" placeholder="Delivery days" value={newService.delivery_days} onChange={(e) => setNewService({ ...newService, delivery_days: e.target.value })} data-testid="new-service-delivery" />
                    <Textarea placeholder="Description" value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} data-testid="new-service-description" />
                    <Button type="submit" className="w-full" disabled={savingService} data-testid="add-service-button">
                      <Plus className="w-4 h-4 mr-2" />
                      {savingService ? 'Adding…' : 'Add Service'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="mt-6 max-w-2xl">
              <CardHeader>
                <CardTitle>Photos</CardTitle>
                <CardDescription>Your cover image shows at the top of your public profile; your profile photo shows on listing cards and reviews.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <div className="text-sm font-medium mb-2">Cover Image</div>
                  <div className="relative h-32 rounded-md overflow-hidden bg-muted mb-3">
                    {profile?.cover_photo_path ? (
                      <img src={profile.cover_photo_path} alt="Cover" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No cover image yet</div>
                    )}
                  </div>
                  <input type="file" accept="image/*" ref={coverInputRef} className="hidden" onChange={(e) => handleCoverFileSelected(e.target.files?.[0])} />
                  <Button type="button" variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover} data-testid="upload-cover-button">
                    <ImagePlus className="w-4 h-4 mr-2" />
                    {uploadingCover ? 'Uploading…' : 'Upload Cover Image'}
                  </Button>
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">Profile Photo</div>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-muted flex items-center justify-center shrink-0">
                      {user?.avatar_path ? (
                        <img src={user.avatar_path} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        <UserRound className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <input type="file" accept="image/*" ref={avatarInputRef} className="hidden" onChange={(e) => handleAvatarFileSelected(e.target.files?.[0])} />
                      <Button type="button" variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar} data-testid="upload-avatar-button">
                        <ImagePlus className="w-4 h-4 mr-2" />
                        {uploadingAvatar ? 'Uploading…' : 'Upload Profile Photo'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6 max-w-2xl">
              <CardHeader>
                <CardTitle>Edit Profile</CardTitle>
                <CardDescription>This is what clients see when they browse photographers.</CardDescription>
              </CardHeader>
              <CardContent>
                {profileForm && (
                  <form className="space-y-4" onSubmit={handleSaveProfile}>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Business Name</label>
                      <Input value={profileForm.business_name || ''} onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })} data-testid="profile-business-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">City</label>
                        <Input value={profileForm.city || ''} onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })} data-testid="profile-city" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">State</label>
                        <Input value={profileForm.state || ''} onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })} data-testid="profile-state" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Starting Price (INR)</label>
                      <Input type="number" value={profileForm.starting_price || ''} onChange={(e) => setProfileForm({ ...profileForm, starting_price: e.target.value })} data-testid="profile-starting-price" />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Bio</label>
                      <Textarea className="min-h-[100px]" value={profileForm.bio || ''} onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })} data-testid="profile-bio" />
                    </div>
                    <Button type="submit" disabled={savingProfile} data-testid="save-profile-button">
                      {savingProfile ? 'Saving…' : 'Save Profile'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PhotographerDashboard;
